import React from "react";
import { useReports } from "./useReports";
import OverviewCards from "./components/OverviewCards";
import ComplianceCharts from "./components/ComplianceCharts";
import EmployeeComplianceTable from "./components/EmployeeComplianceTable";
import ExpiredCredentialsTable from "./components/ExpiredCredentialsTable";
import ModuleTabPanel from "../../ui/ModuleTabPanel";
import {
  DocumentsIcon,
  UsersIcon,
  IdCardIcon,
} from "../../Icons";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
} from "../../ui/pageChrome";
import { REPORT_THEME } from "./reportTheme";
import { useUrlTab } from "../../hooks/useUrlTab";

const TABS = [
  { id: "documents", label: "Document Overview", icon: DocumentsIcon },
  { id: "employees", label: "Employee Details", icon: UsersIcon },
  {
    id: "expired-credentials",
    label: "All Expire Credentials",
    icon: IdCardIcon,
  },
];
const REPORT_TAB_IDS = TABS.map((t) => t.id);

const Reports = () => {
  const [activeTab, setActiveTab] = useUrlTab(REPORT_TAB_IDS, "documents");
  const {
    overviewData,
    employeeData,
    loadingOverview,
    loadingEmployees,
    page,
    itemsPerPageLimit,
    handlePageChange,
  } = useReports(activeTab);

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${REPORT_THEME.section.title}`}>
            Compliance Reports
          </h1>
          <p className={PAGE_SUBTITLE}>
            Overview of employee documentation and compliance status
          </p>
        </div>
      </div>

      <ModuleTabPanel
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={REPORT_THEME}
      >
        {activeTab === "documents" ? (
          <div className="space-y-4 animate-fade-in">
            <OverviewCards data={overviewData} loading={loadingOverview} />
            <ComplianceCharts data={overviewData} />
          </div>
        ) : null}

        {activeTab === "employees" ? (
          <div
            className={`bg-white rounded-xl border shadow-sm overflow-hidden animate-fade-in ${REPORT_THEME.section.wrap}`}
          >
            <div
              className={`flex items-center gap-2 h-11 px-4 border-b box-border ${REPORT_THEME.section.head}`}
            >
              <span
                className={`w-0.5 h-4 rounded-full shrink-0 ${REPORT_THEME.section.bar}`}
                aria-hidden
              />
              <h2
                className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${REPORT_THEME.section.title}`}
              >
                Employee Details
              </h2>
              {typeof employeeData?.total === "number" ? (
                <span
                  className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${REPORT_THEME.section.badge}`}
                >
                  {employeeData.total}
                </span>
              ) : null}
            </div>
            <EmployeeComplianceTable
              data={employeeData}
              page={page}
              totalPages={employeeData.totalPages}
              onPageChange={handlePageChange}
              loading={loadingEmployees}
              totalItems={employeeData.total}
              itemsPerPage={itemsPerPageLimit}
            />
          </div>
        ) : null}

        {activeTab === "expired-credentials" ? (
          <div
            className={`bg-white rounded-xl border shadow-sm overflow-hidden animate-fade-in ${REPORT_THEME.section.wrap}`}
          >
            <div
              className={`flex items-center gap-2 h-11 px-4 border-b box-border ${REPORT_THEME.section.head}`}
            >
              <span
                className={`w-0.5 h-4 rounded-full shrink-0 ${REPORT_THEME.section.bar}`}
                aria-hidden
              />
              <h2
                className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${REPORT_THEME.section.title}`}
              >
                Expired Credentials
              </h2>
            </div>
            <ExpiredCredentialsTable />
          </div>
        ) : null}
      </ModuleTabPanel>
    </div>
  );
};

export default Reports;
